import {
	Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
	Card, CardContent, CardMedia, Grid, Chip, IconButton, Divider,
} from '@mui/material';
import {
	Close as CloseIcon,
	LocalShipping as ShippingIcon,
	Inventory as InventoryIcon
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';

const ShipmentSuccessModal = ({
	open,
	onClose,
	successfulShipments = [],
	onOpenSellerCenter,
	inboundOrderId,
	isMobile
}) => {
	const { theme } = useTheme();

	const totalItems = successfulShipments.reduce((sum, item) => sum + (item.qty ?? 0), 0);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			fullScreen={isMobile}
			PaperProps={{
				sx: {
					borderRadius: isMobile ? 0 : 2,
					bgcolor: theme.background.tertiary,
					backgroundImage: 'none',
					maxHeight: "80%"
				}
			}}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					bgcolor: theme.color.primary,
					color: theme.button.color,
					py: 2,
					px: 3,
					fontFamily: theme.fontFamily
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
					<Box>
						<Typography variant="h6" component="div" sx={{ fontWeight: 600, fontFamily: theme.fontFamily }}>
							Shipment Delivered Successfully
						</Typography>
						<Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5, fontFamily: theme.fontFamily }}>
							Inbound Order ID: {inboundOrderId}
						</Typography>
					</Box>
				</Box>
				<IconButton
					onClick={onClose}
					sx={{
						color: theme.button.color,
						'&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
					}}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ p: 0 }}>
				<Box sx={{ p: 3, bgcolor: theme.background.homeSection, fontFamily: theme.fontFamily }}>
					<Grid container spacing={3}>
						<Grid item xs={12} sm={6}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										bgcolor: theme.color.primary,
										color: theme.button.color,
										fontFamily: theme.fontFamily
									}}
								>
									<InventoryIcon />
								</Box>
								<Box>
									<Typography variant="h4" sx={{ fontWeight: 700, color: theme.text.primary, fontFamily: theme.fontFamily }}>
										{successfulShipments.length}
									</Typography>
									<Typography variant="body2" color={theme.text.secondary} sx={{ fontFamily: theme.fontFamily }}>
										Items Delivered
									</Typography>
								</Box>
							</Box>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										bgcolor: theme.accent.success,
										color: 'white',
										fontFamily: theme.fontFamily
									}}
								>
									<ShippingIcon />
								</Box>
								<Box>
									<Typography variant="h4" sx={{ fontWeight: 700, color: theme.text.primary, fontFamily: theme.fontFamily }}>
										{totalItems}
									</Typography>
									<Typography variant="body2" color={theme.text.secondary} sx={{ fontFamily: theme.fontFamily }}>
										Total Quantity
									</Typography>
								</Box>
							</Box>
						</Grid>
					</Grid>
				</Box>
				<Divider />
				<Box sx={{ p: 3 }}>
					<Typography variant="h6" sx={{ mb: 3, color: theme.text.primary, fontWeight: 600, fontFamily: theme.fontFamily }}>
						Delivered Items
					</Typography>
					<Grid container spacing={2}>
						{successfulShipments.map((item, index) => (
							<Grid item xs={12} key={index}>
								<Card
									elevation={0}
									sx={{
										border: 1,
										borderColor: theme.border.medium,
										bgcolor: theme.background.tertiary,
										transition: 'all 0.2s ease-in-out',
										fontFamily: theme.fontFamily,
										'&:hover': {
											borderColor: theme.color.primary,
											transform: 'translateY(-1px)',
											boxShadow: 2
										}
									}}
								>
									<CardContent sx={{ p: 2 }}>
										<Grid container spacing={2} alignItems="center">
											<Grid item xs={12} sm={3} md={2}>
												<Box sx={{ position: 'relative' }}>
													<CardMedia
														component="img"
														sx={{
															width: '100%',
															height: 80,
															objectFit: 'cover',
															borderRadius: 1,
															bgcolor: theme.background.secondary
														}}
														image={item.image ?? ""}
														alt={`Product ${item.sku}`}
													/>
													<Chip
														size="small"
														label={`Qty: ${item.qty}`}
														sx={{
															position: 'absolute',
															top: -8,
															right: -8,
															bgcolor: theme.accent.success,
															color: 'white',
															fontWeight: 600,
															fontSize: '0.75rem',
															fontFamily: theme.fontFamily
														}}
													/>
												</Box>
											</Grid>
											<Grid item xs={12} sm={9} md={10}>
												<Box>
													<Typography
														variant="subtitle1"
														sx={{
															color: theme.text.primary,
															mb: 1,
															fontFamily: theme.fontFamily
														}}
													>
														<strong>Product Name:</strong> {item.productName}
													</Typography>
													<Typography
														variant="subtitle2"
														sx={{
															color: theme.text.primary,
															mb: 1,
															fontFamily: theme.fontFamily
														}}
													>
														<strong>SKU:</strong> {item.sku}
													</Typography>

													<Grid container spacing={2}>
														<Grid item xs={12} sm={6}>
															<Typography variant="body2" color={theme.text.secondary} sx={{ fontFamily: theme.fontFamily }}>
																<strong>Item ID:</strong> {item.itemId}
															</Typography>
														</Grid>
														<Grid item xs={12} sm={6}>
															<Typography variant="body2" color={theme.text.secondary} sx={{ fontFamily: theme.fontFamily }}>
																<strong>GTIN:</strong> {item.gtin || 'N/A'}
															</Typography>
														</Grid>
													</Grid>
												</Box>
											</Grid>
										</Grid>
									</CardContent>
								</Card>
							</Grid>
						))}
					</Grid>
				</Box>
			</DialogContent>
			<DialogActions
				sx={{
					p: 3,
					bgcolor: theme.background.primary,
					gap: 2,
					flexDirection: isMobile ? 'column' : 'row'
				}}
			>
				<Button
					onClick={onClose}
					variant="outlined"
					sx={{
						borderColor: theme.border.medium,
						color: theme.text.primary,
						minWidth: isMobile ? '100%' : 120,
						fontFamily: theme.fontFamily,
						'&:hover': {
							borderColor: theme.color.primary,
							bgcolor: 'rgba(74, 159, 234, 0.04)'
						}
					}}
				>
					Close
				</Button>
				<Button
					onClick={() => onOpenSellerCenter(inboundOrderId)}
					variant="contained"
					startIcon={<ShippingIcon />}
					sx={{
						bgcolor: theme.color.primary,
						color: theme.button.color,
						minWidth: isMobile ? '100%' : 180,
						fontFamily: theme.fontFamily,
						'&:hover': {
							bgcolor: theme.color.secondary
						}
					}}
				>
					View Shipment on Seller Center
				</Button>
			</DialogActions>
		</Dialog >
	);
};

export default ShipmentSuccessModal;